import type { PickByValue } from 'utility-types'

import { objectEntries } from './Entries'
import type { AnyFunction, ReadonlyRecord } from './Types'

type OnlyFunctions<T> = PickByValue<T, AnyFunction>
type FunctionParts<T> = { [P in keyof OnlyFunctions<T>]: Pick<T, P> | T[P] }
type SurrogateFunction = (...args: readonly unknown[]) => unknown

const createSurrogateFunction = <T extends string>(part: ReadonlyRecord<T, AnyFunction>, methodName: T): SurrogateFunction => {
    return (...args) => part[methodName](...args)
}

/**
 * @returns a mixin of T, but only with the function implementations
 */
export const createMixin = <T>(parts: FunctionParts<T>): OnlyFunctions<T> =>
    objectEntries(parts).reduce<OnlyFunctions<T>>(
        (r, [methodName, part]) => ({
            ...r,
            [methodName]:
                /**
                 * Check if part is a function or a class implementation
                 *
                 * The castings here are fine if and only if the type constraints provided above work as expected
                 * @see {OnlyFunctions}
                 */
                part instanceof Function
                    ? (part as SurrogateFunction)
                    : createSurrogateFunction(part as ReadonlyRecord<string, AnyFunction>, methodName as string),
        }),
        {} as OnlyFunctions<T>
    )
